import api from "../../../Services/api";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import MenuLateral from "../../../Componentes/MenuLateral";
import MenuSuperior from "../../../Componentes/MenuSuperior";
import { useAuthContext } from "../../../context/authContext";
import { useOperador } from "../../../Hooks/useOperador";
import { useNavigate, useParams } from "react-router-dom";
import debounce from "lodash.debounce";
import { FiSearch, FiX, FiChevronLeft, FiChevronRight, FiMove, FiCheck, FiChevronDown, FiArrowUp, FiArrowDown } from "react-icons/fi";
import { IoReturnDownBackOutline } from "react-icons/io5";
import { MdModeEdit, MdPlaylistAdd } from "react-icons/md";
import AccordionSecao from "../../../Componentes/chkSecao/accordionSecao";
import CadastroSecaoModal from "./cadastroSecaoModal";
import Swal from "sweetalert2";
import "./index.css";
import { Heading5 } from "lucide-react";

const AuditoriaSecao = () => {

  const { IDClienteAuditoria } = useParams();
  const [cliente, setCliente] = useState(null);
  const [secoes, setSecoes] = useState([]);
  const [total, setTotal] = useState(0);

  const [showModalSecao, setShowModalSecao] = useState(false);
  const [secaoEditando, setSecaoEditando] = useState(null); // null = nova seção

  const [page, setPage] = useState(1);
  const limit = 20;

  // filtro digitado e filtro aplicável são separados
  const [filter, setFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");

  const [openAccordion, setOpenAccordion] = useState(null);
  const [openGrupoAccordion, setOpenGrupoAccordion] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isReordenando, setIsReordenando] = useState(false);
  const [secoesOrdenadas, setSecoesOrdenadas] = useState([]);
  const [salvandoOrdem, setSalvandoOrdem] = useState(false);
  const [contextoReordenacao, setContextoReordenacao] = useState(null); // "raiz" | "subsecao"
  const [grupoReordenandoId, setGrupoReordenandoId] = useState(null);
  const estaFiltrando = Boolean(debouncedFilter?.trim());

  const { authReady } = useAuthContext();
  const { IDOperador, nome, operador } = useOperador();

  const navigate = useNavigate();
  let idFormatado = String(IDOperador).padStart(6, '0');
  const clienteId = Number(IDClienteAuditoria);
  const isAdmin = useMemo(() => {
    if (!operador) return false;

    // Administrador real
    if (operador.permissaoPrincipal === 99) return true;

    // Gerente Auditoria (admin funcional da tela)
    if (
      operador.permissaoPrincipal === 5 &&
      operador.cargos?.some((c) => c.descricao === "Gerente Auditoria")
    ) {
      return true;
    }

    return false;
  }, [operador]);



  const ordenarPorOrdem = useCallback((lista) => {
    return [...lista].sort((a, b) => {
      const ordemA = a.ordem ?? 0;
      const ordemB = b.ordem ?? 0;
      if (ordemA !== ordemB) return ordemA - ordemB;
      return (a.IDSecao ?? 0) - (b.IDSecao ?? 0);
    });
  }, []);

  const secoesOrdenadasPorOrdem = useMemo(() => {
    return ordenarPorOrdem(secoes);
  }, [secoes, ordenarPorOrdem]);

  const listaRaizNatural = useMemo(() => {
    return secoesOrdenadasPorOrdem.filter((secao) => !secao.IDSecaoPai);
  }, [secoesOrdenadasPorOrdem]);

  const subSecoesPorPai = useMemo(() => {
    const mapa = new Map();

    secoesOrdenadasPorOrdem.forEach((secao) => {
      if (!secao.IDSecaoPai) return;
      const chavePai = Number(secao.IDSecaoPai);
      const listaAtual = mapa.get(chavePai) || [];
      listaAtual.push(secao);
      mapa.set(chavePai, listaAtual);
    });

    mapa.forEach((lista, chave) => {
      mapa.set(chave, ordenarPorOrdem(lista));
    });

    return mapa;
  }, [secoesOrdenadasPorOrdem, ordenarPorOrdem]);

  const podeReordenar = useMemo(() => {
    if (debouncedFilter) return false;

    if (openGrupoAccordion) {
      const listaSubSecoes = subSecoesPorPai.get(Number(openGrupoAccordion)) || [];
      return listaSubSecoes.length > 1;
    }

    return listaRaizNatural.length > 1;
  }, [debouncedFilter, openGrupoAccordion, subSecoesPorPai, listaRaizNatural]);

  const handleDebounce = useCallback(
    debounce((value) => {
      setDebouncedFilter(value);
      setPage(1); // Sempre volta pra primeira página quando filtra
    }, 500),
    []
  );

  // Quando o usuário digita -> dispara debounce
  const onChangeFiltro = (e) => {
    const value = e.target.value;
    setFilter(value);
    handleDebounce(value);
  };

  const handleVoltar = () => {
    navigate("/Servicos/" + clienteId);
  };

  useEffect(() => {
    if (authReady) {
      if (!operador) {
        navigate("/");
      } else {
        buscaCliente(clienteId);
      }
    }
  }, [authReady, operador, clienteId]);

  useEffect(() => {
    if (!cliente) return;

    fetchSecoes(clienteId);
  }, [page, debouncedFilter, cliente]);

  useEffect(() => {
    setPage(1);
  }, [debouncedFilter]);

  useEffect(() => {
    if (isReordenando) {
      if (contextoReordenacao === "subsecao" && grupoReordenandoId) {
        setSecoesOrdenadas(
          subSecoesPorPai.get(Number(grupoReordenandoId)) || []
        );
      } else {
        setSecoesOrdenadas(listaRaizNatural);
      }
      setOpenAccordion(null);
    }
  }, [isReordenando, contextoReordenacao, grupoReordenandoId, subSecoesPorPai, listaRaizNatural]);

  useEffect(() => {
    if (isReordenando && !podeReordenar) {
      setIsReordenando(false);
      setSecoesOrdenadas([]);
      setContextoReordenacao(null);
      setGrupoReordenandoId(null);
    }
  }, [isReordenando, podeReordenar]);


  useEffect(() => {
    return () => {
      handleDebounce.cancel();
    };
  }, [handleDebounce]);

  const clearFilter = () => {
    setFilter("");
    setDebouncedFilter("");
    setPage(1);
  };

  const abrirModalNovaSecao = () => {
    if (!isAdmin) return;
    setSecaoEditando(null);
    setShowModalSecao(true);
  };

  const abrirModalEditarSecao = (IDSecao) => {
    setSecaoEditando(IDSecao);
    setShowModalSecao(true);
  };

  const iniciarReordenacao = () => {
    if (!isAdmin || !podeReordenar) return;

    if (openGrupoAccordion) {
      setContextoReordenacao("subsecao");
      setGrupoReordenandoId(Number(openGrupoAccordion));
      setSecoesOrdenadas(subSecoesPorPai.get(Number(openGrupoAccordion)) || []);
    } else {
      setContextoReordenacao("raiz");
      setGrupoReordenandoId(null);
      setSecoesOrdenadas(listaRaizNatural);
    }

    setIsReordenando(true);
  };

  const cancelarReordenacao = () => {
    setIsReordenando(false);
    setSecoesOrdenadas([]);
    setContextoReordenacao(null);
    setGrupoReordenandoId(null);
  };

  const moverSecao = (secaoId, direction) => {
    setSecoesOrdenadas((prev) => {
      const index = prev.findIndex((item) => item.IDSecao === secaoId);
      if (index < 0) return prev;
      const destino = index + direction;
      if (destino < 0 || destino >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(destino, 0, item);
      return next;
    });
  };

  const salvarReordenacao = async () => {
    if (!isAdmin || !secoesOrdenadas.length) return;

    try {
      setSalvandoOrdem(true);
      Swal.fire({
        title: "Salvando ordem...",
        text: "Aguarde enquanto atualizamos as seções.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const atualizadas = secoesOrdenadas.map((secao, index) => ({
        ...secao,
        ordem: index + 1,
      }));

      await Promise.all(
        atualizadas.map((secao) =>
          api.put(`/chksecao/${secao.IDSecao}`, {
            IDCliente: secao.IDCliente ?? clienteId,
            IDSecaoPai: secao.IDSecaoPai ?? null,
            descricao: secao.descricao,
            ordem: secao.ordem ?? null,
            situacao: secao.situacao ?? 1,
          })
        )
      );

      Swal.close();

      await Swal.fire({
        icon: "success",
        title: "Ordem salva!",
        text: "A nova ordem foi aplicada com sucesso.",
      });

      setIsReordenando(false);
      setSecoesOrdenadas([]);
      setContextoReordenacao(null);
      setGrupoReordenandoId(null);
      fetchSecoes(clienteId);
    } catch (error) {
      console.error("Falha ao salvar ordem das seções", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível salvar a nova ordem.",
      });
    } finally {
      setSalvandoOrdem(false);
    }
  };



  const buscaCliente = async (IDCliente) => {
    if (!IDCliente) return null;
    try {
      const res = await api.get(`/cliente/${IDCliente}`);
      setCliente(res.data.Cliente);
    } catch (err) {
      console.error("Erro ao buscar cliente:", err);
      setCliente(null);
    }
  };

  const buscaSecoesCliente = async ({
    page = 1,
    limit = 20,
    IDCliente,
    filter = "",
    operador,
  }) => {
    const params = {
      page,
      limit,
      IDCliente,
      filter,
    };

    // Se NÃO for administrador, filtra pelas seções que o operador tem acesso
    const isAdmin =
      operador?.permissaoPrincipal === 99 ||
      operador?.cargos?.some((c) => c.descricao === "Administrador");

    if (!isAdmin && operador?.IDOperador) {
      params.IDOperador = operador.IDOperador;
    }

    const response = await api.get("/chksecoesstats", { params });
    return response.data;
  };


  async function fetchSecoes(IDCliente) {
    try {
      setLoading(true);

      const result = await buscaSecoesCliente(
        estaFiltrando
          ? {
            page: 1,
            limit: 9999,
            IDCliente,
            filter: "",
            operador,
          }
          : {
            page,
            limit,
            IDCliente,
            filter: debouncedFilter,
            operador,
          }
      );

      setSecoes(result.data);
      setTotal(estaFiltrando ? result.data.length : result.total);
    } catch (error) {
      console.error("Erro ao carregar seções", error);
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.ceil(total / limit);

  const termoFiltro = debouncedFilter.trim().toLowerCase();
  const listaRaizExibida =
    isReordenando && contextoReordenacao === "raiz"
      ? secoesOrdenadas
      : listaRaizNatural;

  const listaRaizFiltrada = useMemo(() => {
    if (!estaFiltrando) return listaRaizExibida;

    return listaRaizExibida.filter((secao) => {
      const descricaoRaiz = String(secao.descricao || "").toLowerCase();
      if (descricaoRaiz.includes(termoFiltro)) return true;

      const subSecoes = subSecoesPorPai.get(Number(secao.IDSecao)) || [];
      return subSecoes.some((sub) =>
        String(sub.descricao || "").toLowerCase().includes(termoFiltro)
      );
    });
  }, [estaFiltrando, listaRaizExibida, termoFiltro, subSecoesPorPai]);

  return (
    <div className="menu-container-menu auditoria-secao-page">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <MenuSuperior userId={idFormatado} userName={nome} />

        <div className="mb-4">
          <h5 style={{ width: '100%', textAlign: 'center', paddingTop: '30px' }}>
            AUDITORIA
          </h5>
          <h3>{cliente?.nome} - {cliente?.uf}</h3>
        </div>

        <div className="container mt-2 px-5">
          <div className="menuListArea">
            <div className="btn-voltarArea">
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm btn-voltar"
                  onClick={handleVoltar}
                >
                  <IoReturnDownBackOutline size={18} style={{ marginRight: 6 }} />
                  Voltar
                </button>
              </div>
            </div>
            <div className="search-container">
              <span className="search-icon">
                <FiSearch />
              </span>
              <input
                type="text"
                className="search-input"
                placeholder="Buscar seção..."
                value={filter}
                onChange={onChangeFiltro}
                disabled={isReordenando || salvandoOrdem}
              />
              {filter.length > 0 && (
                <button className="clear-btn" onClick={clearFilter} disabled={isReordenando || salvandoOrdem}>
                  <FiX />
                </button>
              )}
            </div>

            <div className="mainMenuList">
              <div className="actions">
                {isAdmin && (
                  <>
                    <button
                      className="btn-primary btnMainMenu"
                      onClick={abrirModalNovaSecao}
                      disabled={isReordenando || salvandoOrdem}
                    >
                      <MdPlaylistAdd style={{ fontSize: 20 }} /> Nova seção ou grupo
                    </button>

                    {!isReordenando && (
                      <button
                        className="btn-primary btnMainMenu"
                        onClick={iniciarReordenacao}
                        disabled={!podeReordenar || loading}
                      >
                        <FiMove style={{ fontSize: 20 }} /> Reordenar
                      </button>
                    )}

                    {isReordenando && (
                      <>
                        <button
                          className="btn-primary btnMainMenu"
                          onClick={salvarReordenacao}
                          disabled={salvandoOrdem}
                        >
                          <FiCheck style={{ fontSize: 20 }} /> Salvar ordem
                        </button>

                        <button
                          className="btn-secondary btn-cancel-reorder btnMainMenu"
                          onClick={cancelarReordenacao}
                          disabled={salvandoOrdem}
                        >
                          <FiX style={{ fontSize: 20 }} /> Cancelar
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          {loading && <p className="loading-text">Carregando...</p>}

          {!loading && !secoes.length && (
            <p className="empty-text">Nenhuma seção encontrada.</p>
          )}
          {!loading && !!secoes.length && !listaRaizFiltrada.length && (
            <p className="empty-text">Nenhuma seção encontrada.</p>
          )}
          <ul className="secao-list">
            {listaRaizFiltrada.map((secao, index) => {
              const ehGrupo = Number(secao.qntSubSecoes || 0) > 0;
              const subSecoes = subSecoesPorPai.get(Number(secao.IDSecao)) || [];
              const totalAtivasGrupo = Number(secao.qntQuestoesAtivasGrupo || 0);
              const respondidasAtivasGrupo = Number(secao.qntRespostasAtivasGrupo || 0);
              const percentualGrupo =
                totalAtivasGrupo > 0
                  ? Math.round((respondidasAtivasGrupo / totalAtivasGrupo) * 100)
                  : 0;

              if (ehGrupo) {
                const subSecoesFiltradas = estaFiltrando
                  ? subSecoes.filter((sub) =>
                    String(sub.descricao || "").toLowerCase().includes(termoFiltro)
                  )
                  : subSecoes;

                const subSecoesExibidas =
                  isReordenando &&
                    contextoReordenacao === "subsecao" &&
                    Number(grupoReordenandoId) === Number(secao.IDSecao)
                    ? secoesOrdenadas
                    : subSecoesFiltradas;

                const grupoExpandido =
                  estaFiltrando || openGrupoAccordion === secao.IDSecao;

                return (
                  <li
                    key={secao.IDSecao}
                    className={`secao-grupo ${grupoExpandido ? "open" : ""} ${percentualGrupo === 100 && totalAtivasGrupo > 0 ? "secao-grupo-completo" : ""}`}
                  >
                    <div className="secao-grupo-header">
                      {isReordenando && contextoReordenacao === "raiz" && (
                        <div className="secao-grupo-reorder">
                          <button
                            type="button"
                            className="secao-grupo-reorder-btn"
                            onClick={() => moverSecao(secao.IDSecao, -1)}
                            disabled={index === 0 || salvandoOrdem}
                          >
                            <FiArrowUp />
                          </button>
                          <button
                            type="button"
                            className="secao-grupo-reorder-btn"
                            onClick={() => moverSecao(secao.IDSecao, 1)}
                            disabled={index === listaRaizExibida.length - 1 || salvandoOrdem}
                          >
                            <FiArrowDown />
                          </button>
                        </div>
                      )}
                      <button
                        type="button"
                        className="secao-grupo-toggle"
                        onClick={() => {
                          if (isReordenando || estaFiltrando) return;
                          setOpenGrupoAccordion((prev) =>
                            prev === secao.IDSecao ? null : secao.IDSecao
                          );
                        }}
                      >
                        <span className="secao-grupo-title">
                          {secao.ordem ? `${secao.ordem} - ` : ""}
                          {secao.descricao}
                        </span>
                        <div className="secao-grupo-progress">
                          <div
                            className="secao-grupo-progress-bar"
                            title={`${respondidasAtivasGrupo}/${totalAtivasGrupo} questões ativas respondidas`}
                          >
                            <div
                              className="secao-grupo-progress-fill"
                              style={{ width: `${percentualGrupo}%` }}
                            />
                          </div>
                          <span
                            className="secao-grupo-progress-text"
                            title={`${respondidasAtivasGrupo}/${totalAtivasGrupo} questões ativas respondidas`}
                          >
                            {percentualGrupo}%
                          </span>
                        </div>
                        <FiChevronDown
                          className={`secao-grupo-chevron ${grupoExpandido ? "rotated" : ""}`}
                        />
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          className="secao-grupo-edit btn-primary"
                          onClick={() => abrirModalEditarSecao(secao.IDSecao)}
                        >
                          <MdModeEdit style={{ marginRight: 6 }} />
                          Editar grupo
                        </button>
                      )}
                    </div>

                    {(grupoExpandido ||
                      (isReordenando &&
                        contextoReordenacao === "subsecao" &&
                        Number(grupoReordenandoId) === Number(secao.IDSecao))) && (
                        <div className="secao-subsecoes">
                          {!subSecoesExibidas.length && (
                            <p className="empty-text">Nenhuma subseção encontrada.</p>
                          )}
                          {subSecoesExibidas.map((subSecao, subIndex) => (
                            <AccordionSecao
                              key={subSecao.IDSecao}
                              secao={subSecao}
                              isOpen={openAccordion === subSecao.IDSecao}
                              isReordenando={
                                isReordenando &&
                                contextoReordenacao === "subsecao" &&
                                Number(grupoReordenandoId) === Number(secao.IDSecao)
                              }
                              onMoveUp={() => moverSecao(subSecao.IDSecao, -1)}
                              onMoveDown={() => moverSecao(subSecao.IDSecao, 1)}
                              disableMoveUp={subIndex === 0 || salvandoOrdem}
                              disableMoveDown={subIndex === subSecoesExibidas.length - 1 || salvandoOrdem}
                              operador={operador}
                              onToggle={() => {
                                if (isReordenando) return;
                                setOpenAccordion(
                                  openAccordion === subSecao.IDSecao ? null : subSecao.IDSecao
                                );
                              }}
                              onEdit={() => abrirModalEditarSecao(subSecao.IDSecao)}
                              onSituacaoChanged={() => fetchSecoes(clienteId)}
                            />
                          ))}
                        </div>
                      )}
                  </li>
                );
              }

              return (
                <AccordionSecao
                  key={secao.IDSecao}
                  secao={secao}
                  isOpen={openAccordion === secao.IDSecao}
                  isReordenando={isReordenando && contextoReordenacao === "raiz"}
                  onMoveUp={() => moverSecao(secao.IDSecao, -1)}
                  onMoveDown={() => moverSecao(secao.IDSecao, 1)}
                  disableMoveUp={
                    contextoReordenacao === "raiz" ? index === 0 || salvandoOrdem : true
                  }
                  disableMoveDown={
                    contextoReordenacao === "raiz"
                      ? index === listaRaizExibida.length - 1 || salvandoOrdem
                      : true
                  }
                  operador={operador}
                  onToggle={() => {
                    if (isReordenando) return;
                    setOpenAccordion(
                      openAccordion === secao.IDSecao ? null : secao.IDSecao
                    );
                  }}
                  onEdit={() => abrirModalEditarSecao(secao.IDSecao)}
                  onSituacaoChanged={() => fetchSecoes(clienteId)}
                />
              );
            })}
          </ul>

          {/* Paginação */}
          {!estaFiltrando && (
            <div className="pagination">
              <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1 || isReordenando}>
                <FiChevronLeft size={18} style={{ marginRight: 4 }} />
              </button>

              <span>Página {page} de {totalPages}</span>

              <button onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages || isReordenando}>
                <FiChevronRight size={18} style={{ marginLeft: 4 }} />
              </button>
            </div>
          )}
        </div>

      </div>
      {showModalSecao && (
        <CadastroSecaoModal
          open={showModalSecao}
          IDCliente={clienteId}
          clienteNome={cliente?.nome}
          clienteUf={cliente?.uf}
          totalSecoes={total}
          IDSecao={secaoEditando} // null = novo
          onClose={() => setShowModalSecao(false)}
          onSaved={() => {
            setShowModalSecao(false);
            fetchSecoes(clienteId); // Recarrega lista após salvar
          }}
        />
      )}
    </div>
  );
};

export default AuditoriaSecao;


